import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SubmitButtonWrapper } from '../styled/Form/SubmitButtonWrapper';
import PrimaryBtn from '../shared/Buttons/PrimaryButton';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import TextFieldStyled from '../shared/TextFieldStyled';
import { FormControlsWrapper } from '../styled/Form/FormControlsWrapper';
import SelectStyled from '../shared/SelectStyled';
import { AddressesFormProps, AddressType } from '../../models/Addresses';
import { PATTERNS } from '../../utils/constans/constans';

const schema = yup.object().shape({
    phone: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .matches(PATTERNS.PHONE_NUMBER, 'FORM_VALIDATION_ERROR.PHONE'),
    country: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
    postcode: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
    city: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
    address1: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
});

export const EditAddressForm: React.FC<AddressesFormProps> = (
    props: AddressesFormProps
) => {
    const { address, setIsLoading, submitForm } = props;
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isDirty },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
    });
    const [submitIsLoading, setSubmitIsLoading] = useState(false);
    const [country, setCountry] = useState(address.data.country);
    const { t } = useTranslation();
    // const { countries } = useCountriesSWR();

    useEffect(() => {
        setSubmitIsLoading(setIsLoading);
    }, [setIsLoading]);

    const handleCountryChange = (event: SelectChangeEvent) => {
        setCountry(event.target.value as string);
    };

    const getTranslate = (key) => {
        return t(key).toString();
    };

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        setSubmitIsLoading(true);
        submitForm(data);
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormControlsWrapper>
                    {/* Postcode */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.POSTAL_CODE'
                            ).toString()}
                            error={!!errors.postcode}
                            defaultValue={address ? address.data.postcode : ''}
                            {...register('postcode')}
                            helperText={
                                errors.postcode
                                    ? getTranslate(errors.postcode.message)
                                    : ' '
                            }
                        />
                    </FormControl>
                    {/* City */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.CITY'
                            ).toString()}
                            error={!!errors.city}
                            defaultValue={address ? address.data.city : ''}
                            {...register('city')}
                            helperText={
                                errors.city
                                    ? getTranslate(errors.city.message)
                                    : ' '
                            }
                        />
                    </FormControl>
                </FormControlsWrapper>
                {/* Address 1 */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t(
                            'PROFILE.PROFILE_DIALOG.FORM_CONTENT.ADDRESS1'
                        ).toString()}
                        error={!!errors.address1}
                        defaultValue={address ? address.data.address1 : ''}
                        {...register('address1')}
                        helperText={
                            errors.address1
                                ? getTranslate(errors.address1.message)
                                : ' '
                        }
                    />
                </FormControl>
                {/* Address 2 */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t(
                            'PROFILE.PROFILE_DIALOG.FORM_CONTENT.ADDRESS2'
                        ).toString()}
                        defaultValue={address ? address.data.address2 : ''}
                        {...register('address2')}
                        helperText={' '}
                    />
                </FormControl>
                {/* Country */}
                <FormControl fullWidth variant="filled">
                    <InputLabel
                        sx={{
                            color: '#999999',
                        }}
                    >
                        {t('REGISTRATION_FORM_FIELDS.COUNTRY').toString()}
                    </InputLabel>
                    <SelectStyled
                        label={t('REGISTRATION_FORM_FIELDS.COUNTRY').toString()}
                        {...register('country', {
                            onChange: (e) => handleCountryChange(e),
                        })}
                        value={country}
                    >
                        {/*{countries &&*/}
                        {/*    countries.map((countryCode) => (*/}
                        {/*        <MenuItem key={countryCode} value={countryCode}>*/}
                        {/*            {t('COUNTRIES.' + countryCode)}*/}
                        {/*        </MenuItem>*/}
                        {/*    ))}*/}
                        <MenuItem value={'HU'}>
                            {t('COUNTRIES.HU', { ns: 'countries' })}
                        </MenuItem>
                    </SelectStyled>
                    <FormHelperText error={!!errors.country}>
                        &#8203;
                        {errors.country && getTranslate(errors.country.message)}
                    </FormHelperText>
                </FormControl>
                {/* Phone */}
                <FormControl fullWidth>
                    <TextFieldStyled
                        label={t(
                            'PROFILE.PROFILE_DIALOG.FORM_CONTENT.PHONE'
                        ).toString()}
                        error={!!errors.phone}
                        defaultValue={address ? address.data.phone : ''}
                        {...register('phone')}
                        helperText={
                            errors.phone
                                ? getTranslate(errors.phone.message)
                                : ' '
                        }
                    />
                </FormControl>
                <SubmitButtonWrapper>
                    <PrimaryBtn
                        type="submit"
                        disabled={!isDirty || submitIsLoading}
                    >
                        {t(
                            'PROFILE.PROFILE_DIALOG.FORM_CONTENT.SEND'
                        ).toString()}
                    </PrimaryBtn>
                </SubmitButtonWrapper>
            </form>
        </>
    );
};

type FormValues = {
    type: AddressType;
    phone: string;
    country: string;
    postcode: string;
    city: string;
    address1: string;
    address2: string;
};
